import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  PlanYearToggle,
  YOUR_COST_RANGE_CAPTION,
  cn,
  costRange,
  nextYearMarker,
  type M3pProjection,
  type PlanYears,
} from "@dearbill/shared";
import { ArrowRightIcon } from "../icons";
import { CostFigure } from "./CostFigure";
import { unavailableCopy } from "./estimateCopy";

interface SavingsSummaryProps {
  /** The projection for `planYear`. */
  m3p: M3pProjection | null;
  years: PlanYears;
  planYear: number;
  onPlanYearChange: (year: number) => void;
}

/**
 * The M3P estimate as two tiles: the one-month pharmacy bill without the plan
 * beside the monthly payment with it, followed by the estimate's notes. The
 * copy never says M3P saves money, since the member pays the same total with
 * or without the plan. The range caption shows while either figure is a range,
 * since it is the page's only explanation of the two bounds. An estimate
 * missing either figure collapses to a single "Still calculating" figure. The
 * link to the Cost Estimate page is the card's last row in both states, and
 * below `lg` it is Home's only way to that page.
 *
 * The header row holds the shared plan-year toggle after the `<h2>`; it
 * renders nothing while `years.next` is null. A next-year projection
 * (`pricesEstimated`) spreads over that year's 12 months and carries the
 * `nextYearMarker` line after the basis line.
 */
export function SavingsSummary({
  m3p,
  years,
  planYear,
  onPlanYearChange,
}: SavingsSummaryProps): ReactNode {
  const priced = m3p !== null && m3p.totalOop !== null && m3p.monthlyPayment !== null;
  return (
    <section className="border-haze flex flex-col gap-4 rounded-2xl border bg-white p-7">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2 className="text-overline text-slate-teal uppercase">Costs spread via M3P</h2>
        <PlanYearToggle years={years} value={planYear} onChange={onPlanYearChange} />
      </div>
      {priced ? (
        <PricedEstimate m3p={m3p} />
      ) : (
        <>
          <CostFigure amount={null} />
          <p className="text-body-sm text-slate-teal">{unavailableCopy(m3p)}</p>
        </>
      )}
      <Link
        to="/cost-estimate"
        className="text-link text-primary-text hover:text-ember-dark inline-flex items-center gap-1 self-start hover:underline"
      >
        See cost estimate
        <ArrowRightIcon size={16} className="shrink-0" />
      </Link>
    </section>
  );
}

function PricedEstimate({ m3p }: { m3p: M3pProjection }): ReactNode {
  const without = costRange(m3p.totalOopMin, m3p.totalOop);
  const monthly = costRange(m3p.monthlyPaymentMin, m3p.monthlyPayment);
  const months = m3p.monthsRemaining;
  const monthsWord = months === 1 ? "month" : "months";
  const year = m3p.contractYear ?? new Date().getFullYear();
  const excluded = m3p.excludedRxcuis.length;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <CostTile
          label="Without M3P"
          amount={without.text}
          caption="At the pharmacy, the month you fill all your medications"
        />
        <CostTile
          label="With M3P"
          accent
          amount={monthly.text}
          unit="/month"
          caption={
            m3p.pricesEstimated
              ? `Spread over the ${months} ${monthsWord} of ${year}`
              : `Spread over the ${months} ${monthsWord} left in the year`
          }
        />
      </div>
      <div className="text-body-sm text-slate-teal flex flex-col gap-2">
        <p className="text-midnight font-semibold">
          The year costs you the same either way. The plan changes when you pay, not how much. There
          is no interest and no fee.
        </p>
        <p>
          {`Estimated out-of-pocket cost for one fill of each medication under your plan's ${year} cost sharing.`}
        </p>
        {m3p.pricesEstimated && <p>{nextYearMarker(year)}</p>}
        {(without.ranged || monthly.ranged) && <p>{YOUR_COST_RANGE_CAPTION}</p>}
        {excluded > 0 && (
          <p>
            Excludes {excluded} {excluded === 1 ? "medication" : "medications"} not covered by your
            plan.
          </p>
        )}
      </div>
    </>
  );
}

interface CostTileProps {
  label: string;
  /** Draws the tile's border, label and figure in Ember; the caption keeps its color. */
  accent?: boolean;
  amount: string;
  unit?: string;
  caption: string;
}

/**
 * The label's classes compose as a plain string, not with cn(): tailwind-merge
 * can't tell the custom text-body-sm size token from the text-* colors and
 * would drop the size token.
 */
function CostTile({ label, accent = false, amount, unit, caption }: CostTileProps): ReactNode {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-lg border bg-white p-5",
        accent ? "border-ember" : "border-haze",
      )}
    >
      <p className={`text-body-sm font-medium ${accent ? "text-ember" : "text-slate-teal"}`}>
        {label}
      </p>
      <CostFigure amount={amount} unit={unit} tone={accent ? "ember" : "navy"} />
      <p className="text-body-sm text-slate-teal">{caption}</p>
    </div>
  );
}
